import React, {
  memo, useEffect, useReducer
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { createMasterIbAgreement } from "store/actions";

import {
  Row, Col, Button,
  Modal, ModalBody, ModalHeader,
  Input
} from "reactstrap";
import AvFieldSelect from "components/Common/AvFieldSelect";
import validatePositiveInputs from "helpers/validatePositiveInputs";
import { getProductCategoryLabel } from "common/productCategories";

// An empty/cleared numeric input must never be stored as NaN - it used to be
// implicitly guarded by AvField's live "required" validator blocking submit;
// now that these fields are plain, state-controlled inputs, this is the guard.
const toSafeNumber = (raw) => {
  if (raw === "") return 0;
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const InvestorForm = ({ accIdx, acc, value, dispatcher }) => {
  const { t } = useTranslation();
  return <Row key={accIdx}>
    <Row className="justify-content-center mb-2 fw-bold gx-0">
      <Col md="3" className="text-center">{acc.title}</Col>
    </Row>
    <Row>
      <Col md="3" className="text-center">
        {t("PAMM Profit Share")} %
      </Col>
      <Col md="9" className="text-center">
        <Input
          type="text"
          value={value?.pamProfitShare}
          onKeyPress={(e) => validatePositiveInputs(e)}
          onChange={(e) => {
            dispatcher({
              type: "UPDATE_PAMM_PROFIT_SHARE",
              payload: {
                accountTypeId: acc._id,
                pamProfitShare: toSafeNumber(e.target.value),
              },
            });
          }}
        />
      </Col>
    </Row>
    <hr className="my-3" />
  </Row>;
};

const AccountForm = ({ accIdx, acc, products, markups, value, dispatcher }) => {
  const { t } = useTranslation();
  return <Row key={accIdx}>
    <Row className="justify-content-end mb-2 fw-bold gx-0">
      <Col md="3" className="text-center">{acc.title}</Col>
      <Col className="ms-1">{t("Rebate")}</Col>
      <Col>{t("Commission")}</Col>
    </Row>
    {products.map((prod, prodIdx) =>
      <Row key={prodIdx} className="my-1 align-items-center">
        <Col md="3" className="text-center">
          {getProductCategoryLabel(prod)}
        </Col>
        <Col>
          <Input
            type="text"
            value={value?.products?.[prod]?.rebate ?? 0}
            onKeyPress={validatePositiveInputs}
            onChange={(e) => {
              dispatcher({
                type: "UPDATE_PRODUCT_REBATE",
                payload: {
                  accountTypeId: acc._id,
                  product: prod,
                  rebate: toSafeNumber(e.target.value),
                },
              });
            }}
          />
        </Col>
        <Col>
          <Input
            type="text"
            value={value?.products?.[prod]?.commission ?? 0}
            onKeyPress={validatePositiveInputs}
            onChange={(e) => {
              dispatcher({
                type: "UPDATE_PRODUCT_COMMISSION",
                payload: {
                  accountTypeId: acc._id,
                  product: prod,
                  commission: toSafeNumber(e.target.value),
                },
              });
            }}
          />
        </Col>
      </Row>
    )}
    <Row key={accIdx} className="my-1 align-items-center">
      <Col md="3" className="text-center">
        {t("Markup")}
      </Col>
      <Col>
        <AvFieldSelect
          name={`values[${accIdx}].markup`}
          value={value?.markup}
          onChange={(markup) => {
            dispatcher({
              type: "UPDATE_MARKUP",
              payload: {
                accountTypeId: acc._id,
                markup,
              },
            });
          }}
          options={(markups || []).map((obj) => {
            return ({
              label: `${obj}`,
              value: obj
            });
          })}
        />
      </Col>
    </Row>
    <hr className="my-3" />
  </Row>;
};

const MemoizedInvestorForm = memo(InvestorForm);
const MemoizedAccountForm = memo(AccountForm);

const buildInitialValues = (allAccountTypes, products) => (allAccountTypes || [])
  .filter((acc) => acc.type !== "DEMO")
  .map((acc) => {
    const isInvestor = acc.type === "INVESTOR";
    const base = {
      accountTypeId: acc._id,
      markup: "",
      products: (products || []).reduce((acc2, prod) => {
        acc2[prod] = {
          rebate: 0,
          commission: 0 
        };
        return acc2;
      }, {}),
    };
    if (isInvestor) {
      base.pamProfitShare = 0;
    }
    return base;
  });

const AddMasterIbModal = ({ show, toggle, accountTypes: allAccountTypes, products, customerId, markups }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { submitting } = useSelector((state) => state.ibAgreements);

  const [payload, dispatcher] = useReducer((state, action) => {
    switch (action.type) {
      case "RESET":
        return {
          title: "",
          values: [] 
        };
      case "UPDATE_TITLE":
        return {
          ...state,
          title: action.payload 
        };
      case "INIT_VALUES":
        return {
          ...state,
          values: action.payload 
        };
      case "UPDATE_PAMM_PROFIT_SHARE":
        return {
          ...state,
          values: state.values.map((val) => val.accountTypeId === action.payload.accountTypeId
            ? {
              ...val,
              pamProfitShare: action.payload.pamProfitShare 
            }
            : val),
        };
      case "UPDATE_PRODUCT_REBATE":
        return {
          ...state,
          values: state.values.map((val) => {
            if (val.accountTypeId !== action.payload.accountTypeId) return val;
            return {
              ...val,
              products: {
                ...val.products,
                [action.payload.product]: {
                  ...val.products?.[action.payload.product],
                  rebate: action.payload.rebate,
                },
              },
            };
          }),
        };
      case "UPDATE_PRODUCT_COMMISSION":
        return {
          ...state,
          values: state.values.map((val) => {
            if (val.accountTypeId !== action.payload.accountTypeId) return val;
            return {
              ...val,
              products: {
                ...val.products,
                [action.payload.product]: {
                  ...val.products?.[action.payload.product],
                  commission: action.payload.commission,
                },
              },
            };
          }),
        };
      case "UPDATE_MARKUP":
        return {
          ...state,
          values: state.values.map((val) => val.accountTypeId === action.payload.accountTypeId
            ? {
              ...val,
              markup: action.payload.markup 
            }
            : val),
        };
      default:
        return state;
    }
  }, {
    title: "",
    values: [] 
  });

  useEffect(() => {
    !submitting && show && toggle();
  }, [submitting]);

  useEffect(() => {
    if (show && allAccountTypes && allAccountTypes.length > 0) {
      dispatcher({
        type: "INIT_VALUES",
        payload: buildInitialValues(allAccountTypes, products),
      });
    }
  }, [allAccountTypes, products, show]);

  useEffect(() => {
    if (!show) {
      dispatcher({ type: "RESET" });
    }
  }, [show]);

  if (!show || !allAccountTypes) return null;

  const { title, values } = payload;

  return (
    <Modal isOpen={show} centered={true} scrollable={true}>
      <ModalHeader toggle={toggle} tag="h4">
        {t("New Master IB Agreement")}
      </ModalHeader>
      <ModalBody >
        <AvForm
          onValidSubmit={() => {
            dispatch(createMasterIbAgreement({
              customerId,
              title,
              values,
            }));
          }}
        >
          <AvField
            name={"title"}
            label={t("Agreement name")}
            className="mb-3"
            type="text"
            value={title}
            errorMessage={t("Required!")}
            validate={{ required: { value: true } }}
            onChange={(e) => {
              dispatcher({
                type: "UPDATE_TITLE",
                payload: e.target.value,
              });
            }}
          />
          {allAccountTypes?.filter(acc => acc.type !== "DEMO").map((acc, accIdx) => {
            const value = values?.find((val) => val.accountTypeId === acc._id);
            return (
              <React.Fragment key={accIdx}>
                {acc?.type === "INVESTOR" && (
                  <MemoizedInvestorForm acc={acc} accIdx={accIdx} value={value} dispatcher={dispatcher} />
                )}
                <MemoizedAccountForm
                  acc={acc}
                  accIdx={accIdx}
                  products={products}
                  markups={markups}
                  value={value}
                  dispatcher={dispatcher}
                />
              </React.Fragment>
            );
          })}
          <Button type="submit" disabled={submitting}>{t("Submit")}</Button>
        </AvForm>
      </ModalBody>
    </Modal >);
};

export default memo(AddMasterIbModal);
